// src/pages/customer/components/details/tabs/CustomerPackageSalesTab.jsx
import { Badge, Button } from "react-bootstrap";
import { formatDate, formatDateTime, formatInReal } from "../../../../../commons/util/HallAppUtil";

import AppLoading from "../../../../../commons/AppLoading";
import { useEffect } from "react";

/**
 * Status possíveis (ajuste se seu backend tiver outros):
 * ACTIVE, EXPIRED, CANCELED, FINISHED, PENDING
 */
const packageStatusLabel = (status) => {
  const s = String(status || "").toUpperCase();

  switch (s) {
    case "ACTIVE":
      return "Ativo";
    case "EXPIRED":
      return "Expirado";
    case "CANCELED":
      return "Cancelado";
    case "FINISHED":
      return "Finalizado";
    case "PENDING":
      return "Pendente";
    default:
      return status || "—";
  }
};

const packageStatusVariant = (status) => {
  const s = String(status || "").toUpperCase();

  switch (s) {
    case "ACTIVE":
      return "success";
    case "EXPIRED":
      return "dark";
    case "CANCELED":
      return "secondary";
    case "FINISHED":
      return "primary";
    case "PENDING":
      return "warning";
    default:
      return "dark";
  }
};

const SafeLine = ({ show, children }) => (show ? <span>{children}</span> : null);

const getTotalSessions = (items = []) =>
  items.reduce((acc, item) => acc + (Number(item?.quantityTotal) || 0), 0);

const getRemainingSessions = (items = []) =>
  items.reduce((acc, item) => acc + (Number(item?.quantityRemaining) || 0), 0);

export default function CustomerPackageSalesTab({
  customerId,
  show,
  isLoading,
  packages,
  refresh,
}) {
  useEffect(() => {
    if (!show) return;
    refresh?.();
  }, [show, refresh]);

  if (isLoading) {
    return (
      <div className="mt-3">
        <AppLoading />
      </div>
    );
  }

  if (!packages || packages.length === 0) {
    return (
      <div className="hc-cd-panel">
        <div className="hc-cd-panel__title">Pacotes</div>
        <div className="hc-cd-empty">
          <div className="hc-cd-empty__title">Nenhum pacote encontrado</div>
          <div className="hc-cd-empty__subtitle">Esse cliente ainda não possui pacotes comprados.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="hc-cd-panel">
      <div className="d-flex align-items-center justify-content-between gap-2">
        <div className="hc-cd-panel__title mb-0">Pacotes</div>

        <Button size="sm" variant="outline-dark" onClick={refresh}>
          Atualizar
        </Button>
      </div>

      <div className="hc-cd-clubs">
        {packages.map((it, idx) => {
          const key = it.packageSaleId ?? it.packageId ?? idx;

          const statusText = packageStatusLabel(it.saleStatus);
          const statusBg = packageStatusVariant(it.saleStatus);

          const amountText =
            it.soldAmount != null ? formatInReal(Number(it.soldAmount) || 0) : null;

          const totalSessions = getTotalSessions(it.items);
          const remainingSessions = getRemainingSessions(it.items);
          const usedSessions = totalSessions - remainingSessions;

          return (
            <div key={key} className="hc-cd-clubCard">
              {/* TOP */}
              <div className="hc-cd-clubCard__top">
                <div className="hc-cd-clubCard__title text-truncate">
                  {it.packageName || "Pacote"}
                </div>

                <Badge bg={statusBg} pill className="hc-cd-clubCard__status">
                  {statusText}
                </Badge>
              </div>

              {/* MAIN META */}
              <div className="hc-cd-clubCard__meta">
                <SafeLine show={!!amountText}>
                  💰 Valor: <strong>{amountText}</strong>
                </SafeLine>

                <SafeLine show={totalSessions > 0}>
                  📦 Sessões: <strong>{usedSessions}</strong> usadas de{" "}
                  <strong>{totalSessions}</strong>
                </SafeLine>

                <SafeLine show={totalSessions > 0}>
                  ⏳ Restantes: <strong>{remainingSessions}</strong>
                </SafeLine>
              </div>

              {/* DATES */}
              <div className="hc-cd-clubCard__dates">
                <SafeLine show={!!it.soldAt}>🛒 Vendido em: {formatDateTime(it.soldAt)}</SafeLine>
                <SafeLine show={!!it.expiresAt}>⏳ Expira em: {formatDate(it.expiresAt)}</SafeLine>
              </div>

              {/* ITEMS */}
              {it.items && it.items.length > 0 ? (
                <div className="hc-cd-clubCard__notes">
                  <strong>Itens do pacote</strong>

                  <div className="mt-2 d-flex flex-column gap-2">
                    {it.items.map((item, itemIdx) => {
                      const itemKey = item.id ?? `${key}-${itemIdx}`;
                      const total = Number(item.quantityTotal) || 0;
                      const remaining = Number(item.quantityRemaining) || 0;
                      const used = total - remaining;

                      return (
                        <div
                          key={itemKey}
                          className="d-flex align-items-start justify-content-between gap-3 flex-wrap"
                        >
                          <div>
                            <div style={{ fontWeight: 600 }}>
                              {item.serviceName || "Serviço"}
                            </div>
                            <div className="text-muted" style={{ fontSize: 13 }}>
                              Usadas: {used} • Restantes: {remaining} • Total: {total}
                            </div>
                          </div>

                          <Badge bg="light" text="dark" pill>
                            {remaining}/{total}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}